import {z} from "zod"

export const MAX_PAYMENT_PLAN_TIERS = 2

export const validationMessages = {
	oneOffHigherThanPaymentPlan: "Must be higher than all payment plan discounts",
	paymentPlanLowerThanOneOff: "Must be lower than one-off discount",
	weeksGreaterThanPreviousOption: "Must be greater than previous option",
	weeksLessThanNextOption: "Must be less than next option",
	discountLowerThanPreviousOption: "Must be lower than previous option",
	discountGreaterThanNextOption: "Must be greater than next option",
} as const

export const discountRateSchema = z
	.number({message: "Discount must be greater than 0"})
	.min(0.01, "Discount must be greater than 0")
	.max(99.99, "Discount must be less than 100")
	.refine((val) => {
		const decimalPlaces = val.toString().split(".")[1]?.length || 0
		return decimalPlaces <= 3
	}, "Discount can have at most 3 decimal places")

export const maxTimeToPayWeeksSchema = z
	.number({message: "Weeks must be greater than 0"})
	.int("Weeks must be a whole number")
	.positive("Weeks must be greater than 0")

type TierData = {discountRate: number; maxTimeToPayWeeks: number; isEditing?: boolean}
type OneOffData = {discountRate: number; confirmed: boolean}

export function validateTierAgainstContext(
	tier: TierData,
	index: number,
	allTiers: TierData[],
	oneOff?: OneOffData,
): {weeksError: string | null; discountError: string | null} {
	let weeksError: string | null = null
	let discountError: string | null = null

	const previousTier = index > 0 ? allTiers[index - 1] : null
	if (previousTier && !previousTier.isEditing) {
		if (tier.maxTimeToPayWeeks <= previousTier.maxTimeToPayWeeks) {
			weeksError = validationMessages.weeksGreaterThanPreviousOption
		}
		if (tier.discountRate >= previousTier.discountRate) {
			discountError = validationMessages.discountLowerThanPreviousOption
		}
	}

	const nextTier = allTiers[index + 1]
	if (nextTier && !nextTier.isEditing) {
		if (tier.maxTimeToPayWeeks >= nextTier.maxTimeToPayWeeks) {
			weeksError = validationMessages.weeksLessThanNextOption
		}
		if (tier.discountRate <= nextTier.discountRate) {
			discountError = validationMessages.discountGreaterThanNextOption
		}
	}

	if (oneOff?.confirmed && tier.discountRate >= oneOff.discountRate) {
		discountError = validationMessages.paymentPlanLowerThanOneOff
	}

	return {weeksError, discountError}
}

export function validateOneOffAgainstTiers(oneOffDiscount: number, tiers: TierData[]): string | null {
	if (tiers.length === 0) return null

	const highestPlanDiscount = Math.max(...tiers.map((tier) => tier.discountRate))
	if (oneOffDiscount <= highestPlanDiscount) {
		return validationMessages.oneOffHigherThanPaymentPlan
	}
	return null
}

// V2 Schema - supports multiple payment plan tiers
const paymentPlanTierSchemaV2 = z
	.object({
		id: z.string(),
		discountRate: z.number(),
		maxTimeToPayWeeks: z.number(),
		isEditing: z.boolean().optional(),
	})
	.superRefine((data, ctx) => {
		if (data.isEditing) return

		const discountResult = discountRateSchema.safeParse(data.discountRate)
		if (!discountResult.success) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: discountResult.error.issues[0]?.message || "Invalid discount rate",
				path: ["discountRate"],
			})
		}

		const weeksResult = maxTimeToPayWeeksSchema.safeParse(data.maxTimeToPayWeeks)
		if (!weeksResult.success) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: weeksResult.error.issues[0]?.message || "Invalid weeks",
				path: ["maxTimeToPayWeeks"],
			})
		}
	})

export const oneOffSchema = z
	.object({
		discountRate: discountRateSchema,
		confirmed: z.boolean(),
		isEditing: z.boolean().optional(),
	})
	.refine((data) => data.confirmed === true, {
		message: "One-off settlement must be confirmed",
		path: ["confirmed"],
	})

export const offerFormSchemaV2 = z
	.object({
		startDate: z.string().min(1, "Start date is required"),
		endDate: z.string().min(1, "End date is required"),
		notes: z.string().optional(),
		oneOff: oneOffSchema.optional(),
		paymentPlan: z
			.object({
				tiers: z.array(paymentPlanTierSchemaV2),
			})
			.optional(),
		offerTypes: z.string().optional(),
	})
	.refine(
		(data) => {
			if (data.oneOff?.isEditing) return false
			if (data.paymentPlan?.tiers?.some((tier) => tier.isEditing)) return false
			return true
		},
		{
			message: "Please confirm all payment options before creating the offer",
			path: ["offerTypes"],
		},
	)
	.refine(
		(data) => {
			if (data.startDate && data.endDate) {
				return new Date(data.startDate) < new Date(data.endDate)
			}
			return true
		},
		{
			message: "End date must be after start date",
			path: ["endDate"],
		},
	)
	.refine(
		(data) => {
			const hasOneOff = data.oneOff?.discountRate !== undefined && data.oneOff?.confirmed === true
			const hasPlan = (data.paymentPlan?.tiers?.length ?? 0) > 0
			return hasOneOff || hasPlan
		},
		{
			message: "At least one offer type (one-off or payment plan) must be enabled",
			path: ["offerTypes"],
		},
	)
	.refine(
		(data) => {
			if (data.paymentPlan?.tiers) {
				return data.paymentPlan.tiers.length <= MAX_PAYMENT_PLAN_TIERS
			}
			return true
		},
		{
			message: `Maximum of ${MAX_PAYMENT_PLAN_TIERS} payment plan tiers allowed`,
			path: ["paymentPlan", "tiers"],
		},
	)
	.superRefine((data, ctx) => {
		const tiers = data.paymentPlan?.tiers
		if (!tiers || tiers.length < 2) return

		for (let i = 1; i < tiers.length; i++) {
			if (tiers[i].isEditing || tiers[i - 1].isEditing) continue

			if (tiers[i].discountRate >= tiers[i - 1].discountRate) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: validationMessages.discountLowerThanPreviousOption,
					path: ["paymentPlan", "tiers", i, "discountRate"],
				})
			}
			if (tiers[i].maxTimeToPayWeeks <= tiers[i - 1].maxTimeToPayWeeks) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: validationMessages.weeksGreaterThanPreviousOption,
					path: ["paymentPlan", "tiers", i, "maxTimeToPayWeeks"],
				})
			}
		}
	})
	.superRefine((data, ctx) => {
		if (!data.oneOff?.confirmed || !data.paymentPlan?.tiers?.length) return

		const oneOffDiscount = data.oneOff.discountRate
		const tiers = data.paymentPlan.tiers

		const confirmedTiers = tiers.filter((tier) => !tier.isEditing)
		if (confirmedTiers.length === 0) return

		const highestPlanDiscount = Math.max(...confirmedTiers.map((tier) => tier.discountRate))
		if (oneOffDiscount <= highestPlanDiscount) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: validationMessages.oneOffHigherThanPaymentPlan,
				path: ["oneOff", "discountRate"],
			})
		}

		tiers.forEach((tier, i) => {
			if (tier.isEditing) return
			if (tier.discountRate >= oneOffDiscount) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: validationMessages.paymentPlanLowerThanOneOff,
					path: ["paymentPlan", "tiers", i, "discountRate"],
				})
			}
		})
	})

// Original V1 Schema
export const offerFormSchema = z
	.object({
		startDate: z.string().min(1, "Start date is required"),
		endDate: z.string().min(1, "End date is required"),
		notes: z.string().optional(),
		oneOff: z
			.object({
				discountRate: discountRateSchema.optional(),
			})
			.optional(),
		plan: z
			.object({
				discountRate: discountRateSchema.optional(),
				maxTimeToPayWeeks: z.number().int().positive("Maximum time to pay must be greater than 0").optional(),
			})
			.optional(),
	})
	.refine(
		(data) => {
			if (data.startDate && data.endDate) {
				return new Date(data.startDate) < new Date(data.endDate)
			}
			return true
		},
		{
			message: "End date must be after start date",
			path: ["endDate"],
		},
	)
	.refine(
		(data) => {
			const hasOneOff = data.oneOff?.discountRate !== undefined
			const hasPlan = data.plan?.discountRate !== undefined && data.plan?.maxTimeToPayWeeks !== undefined
			return hasOneOff || hasPlan
		},
		{
			message: "At least one offer type (one-off or plan) must be filled",
			path: ["oneOff"],
		},
	)
	.refine(
		(data) => {
			if (data.plan?.discountRate !== undefined && data.plan?.maxTimeToPayWeeks === undefined) {
				return false
			}
			return true
		},
		{
			message: "Maximum time to pay is required when plan discount is specified",
			path: ["plan", "maxTimeToPayWeeks"],
		},
	)

export type OfferFormData = z.infer<typeof offerFormSchema>
export type OfferFormDataV2 = z.infer<typeof offerFormSchemaV2>
